import { FunctionComponent, useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import { Button, Dialog, Table, TableRow, TableCell, Typography, Snackbar } from '../../tailwindcomponents';
import { RootState } from '../../reducer';
import { Edit, Search, Visibility } from '../icons';
import taskApi from '../../api/tasks';
import { HandoverReportDetails, HandoverReportFilter, HandoverReportListItem } from '../../types/handover';
import HandoverReportDialog from './HandoverReportDialog';
import HandoverReportFilters from './HandoverReportFilters';
import { HandoverReportConfig } from './handoverConfig';

type HandoverReportListProps = {
    config: HandoverReportConfig;
    setLoading: Function;
}

/**
 * Report list plus detail dialog for the report-style handovers (Line Planner,
 * Maintenance Control). Everything type specific comes from `config`.
 */
const HandoverReportList: FunctionComponent<HandoverReportListProps> = ({ config, setLoading }) => {
    const store = useSelector((state: RootState) => state);
    const users = store.employees.all_employees;
    const accessGroups = store.user.me.user_clearence_groups;
    const isAdmin = accessGroups.includes(config.editGroup);

    const [reports, setReports] = useState(Array<HandoverReportListItem>());
    const [handoverDialogOpen, setHandoverDialogOpen] = useState(false);
    const [selectedReport, setSelectedReport] = useState<HandoverReportDetails>();
    const [allTails, setAllTails] = useState(Array<string>());

    /** False until GetAll has answered, so an open report cannot be missed. */
    const [reportsLoaded, setReportsLoaded] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    // Filter bar state, only rendered when config.showFilters. Dates start empty and are only sent
    // when set, so a tail or issue search spans all history until the user narrows it.
    const [searchIssue, setSearchIssue] = useState<string>("");
    const [selectedTailNumber, setSelectedTailNumber] = useState<string>("");
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDate, setToDate] = useState<Date | null>(null);

    const [snackbarMessage, setSnackbarMessage] = useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<"error" | "warning" | "info" | "success">("success");

    const hasOpenReport = reports.some(r => r.is_submitted === false);
    const isFiltered = searchIssue !== "" || selectedTailNumber !== "" || fromDate !== null || toDate !== null;

    // `reports` is empty before the list arrives, which makes `hasOpenReport` false, so waiting on
    // `reportsLoaded` is what stops the button appearing next to a report that is still open.
    // A filtered list is a subset for the same reason: an open report can be filtered out of view,
    // and nothing in the database stops a second one being created, so hide the button until the
    // filters are cleared.
    const canCreateReport = isAdmin && reportsLoaded && !isCreating && !hasOpenReport && !isFiltered;

    useEffect(() => {
        setReportsLoaded(false);
        setLoading(true);
        refreshReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config.api]);

    /** GetAll and Search return the same envelope, so both land here. */
    const applyReportList = (res: { status: boolean, message: string, data: Array<HandoverReportListItem> }) => {
        if (res.status === true) {
            setReports(res.data ?? []);
            setReportsLoaded(true);
        } else {
            setSnackbarMessage(res.message ?? `Something went wrong, was not able to load the ${config.dialogTitle} reports`);
            setSnackbarSeverity("error");
        }
        setLoading(false);
    }

    const refreshReports = () => {
        config.api.GetAll().then(applyReportList);
        taskApi.GetAllTailNumbers().then(res => {
            setAllTails(res.data);
        });
    }

    const onFilter = () => {
        // Omit empty fields rather than sending blanks: the backend treats null/empty as "no filter",
        // but leaving them out keeps the request honest about what was actually asked for.
        const filter: HandoverReportFilter = {};
        if (searchIssue !== "") filter.issue = searchIssue;
        if (selectedTailNumber !== "") filter.tail = selectedTailNumber;
        if (fromDate !== null) filter.from_date = fromDate;
        if (toDate !== null) filter.to_date = toDate;

        setLoading(true);
        config.api.Search(filter).then(applyReportList);
    }

    const onClearFilters = () => {
        setSearchIssue("");
        setSelectedTailNumber("");
        setFromDate(null);
        setToDate(null);
        setLoading(true);
        refreshReports();
    }

    const refreshHandover = (reportId: number) => {
        setLoading(true);
        config.api.GetById(reportId).then(res => {
            if (res.status === true) {
                setSelectedReport(res.data);
                setHandoverDialogOpen(true);
            } else {
                setSnackbarMessage(res.message);
                setSnackbarSeverity("error");
            }
            setLoading(false);
        });
    }

    const onCreateNewHandoverReport = () => {
        if (!reportsLoaded || isCreating) return;
        if (hasOpenReport) {
            setSnackbarMessage("You have an open handover report. Please submit or edit the existing report before creating a new one.");
            setSnackbarSeverity("warning");
            return;
        }
        setIsCreating(true);
        setLoading(true);
        config.api.Create().then(res => {
            if (res.status === false) {
                setSnackbarMessage(res.message ?? `Something went wrong, was not able to create a ${config.dialogTitle}`);
                setSnackbarSeverity("error");
            } else {
                refreshHandover(res.data);
                refreshReports();
            }
        }).finally(() => {
            setIsCreating(false);
            setLoading(false);
        });
    }

    const generateTableBody = (row: HandoverReportListItem, index: number) => {
        return (
            <TableRow >
                <TableCell>{row.created_date_text}</TableCell>
                <TableCell>{row.issues_count}</TableCell>
                <TableCell>{users.find(user => user.Id === row.created_by)?.name}</TableCell>
                <TableCell>{users.find(user => user.Id === row.to_be_read_by)?.name}</TableCell>
                <TableCell>
                    {row.is_submitted && <Search className="cursor-pointer" onClick={() => refreshHandover(row.id)} color='primary' />}
                    {!row.is_submitted && isAdmin && <Edit className="cursor-pointer" onClick={() => refreshHandover(row.id)} color='primary' />}
                    {!row.is_submitted && !isAdmin && <Visibility className="cursor-pointer" onClick={() => refreshHandover(row.id)} color='primary' />}
                </TableCell>
            </TableRow>
        )
    }

    return (
        <div className="p-3">
            <Typography variant={'h1'} align={'center'} text={config.title} />
            {config.showFilters &&
                <HandoverReportFilters
                    searchIssue={searchIssue}
                    setSearchIssue={setSearchIssue}
                    selectedTailNumber={selectedTailNumber}
                    setSelectedTailNumber={setSelectedTailNumber}
                    fromDate={fromDate}
                    setFromDate={setFromDate}
                    toDate={toDate}
                    setToDate={setToDate}
                    allTails={allTails}
                    onFilter={onFilter}
                    onClearFilters={onClearFilters}
                />
            }
            {canCreateReport &&
                <Button variant="text" color='primary' onClick={() => onCreateNewHandoverReport()}>Create new</Button>
            }
            <Table
                headers={["Shift date", "Issues", "Created by", "To be read by", "status"]}
                rows={reports}
                generateBody={(row: HandoverReportListItem, index: number) => generateTableBody(row, index)}
                defaultRowsPerPage={25}
                pagination={true}
                skipIndexCol={true}
            />
            <Dialog
                visible={handoverDialogOpen}
                max_width={"xl"}
                strict={false}
                show_options={false}
                onClose={() => setHandoverDialogOpen(false)}
                title={selectedReport?.is_submitted === true ? config.dialogTitle : `Edit ${config.dialogTitle}`}
                context={
                    selectedReport !== undefined &&
                    <HandoverReportDialog
                        config={config}
                        setLoading={setLoading}
                        isEditMode={selectedReport.is_submitted === false && isAdmin}
                        refreshReports={() => refreshReports()}
                        allTails={allTails}
                        report={selectedReport}
                        refresh={(id: number) => refreshHandover(id)}
                        onClose={() => setHandoverDialogOpen(false)}
                    />
                }
            />
            <Snackbar
                onSnackBarClose={() => setSnackbarMessage("")}
                open={snackbarMessage.length > 0}
                message={snackbarMessage}
                autoHide={true}
                horizontalPosition='right'
                severity={snackbarSeverity}
            >
            </Snackbar>
        </div>
    )
}

export default HandoverReportList;
